<template>
  <v-dialog :value="value" max-width="480px" @input="$emit('input', $event)">
    <v-card class="pa-2">
      <v-card-title class="d-flex align-center justify-space-between">
        <span class="text-subtitle-1 font-weight-bold">Изменить эксперта</span>
        <v-btn icon small @click="$emit('input', false)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text v-if="appointment">
        <div class="text-body-2 mb-4">
          <div class="font-weight-medium">{{ appointment.service_type_name }}</div>
          <div class="grey--text text--darken-1">{{ whenText }}</div>
          <div v-if="currentExpertName" class="grey--text text--darken-1">Сейчас: {{ currentExpertName }}</div>
        </div>

        <template v-if="options.length">
          <div class="text-caption grey--text text--darken-1 mb-1">Новый эксперт</div>
          <v-select
            v-model="expertId"
            :items="options"
            item-value="id"
            item-text="name"
            item-disabled="disabled"
            placeholder="Выберите эксперта"
            outlined
            dense
            hide-details
            :menu-props="{ offsetY: true }"
          >
            <template #item="{ item }">
              <v-avatar size="24" color="#483CB9" class="mr-2">
                <img v-if="item.photoUrl" :src="item.photoUrl" alt="">
                <span v-else class="white--text change-expert__initials">{{ item.initials }}</span>
              </v-avatar>
              <div class="change-expert__option">
                <div>{{ item.name }}</div>
                <div class="text-caption grey--text">{{ item.hint }}</div>
              </div>
            </template>
          </v-select>
          <div class="text-caption grey--text mt-2">
            Выбрать можно эксперта со свободным интервалом на это время — запись займёт его интервал,
            а интервал текущего эксперта освободится.
          </div>
        </template>
        <div v-else class="change-expert__empty">
          Нет других экспертов, которые оказывают эту услугу. Добавьте услугу эксперту на вкладке «Команда».
        </div>

        <v-alert v-if="errorMessages.length" type="error" dense text rounded="lg" class="mt-3 mb-0">
          <div v-for="(message, index) in errorMessages" :key="index">{{ message }}</div>
        </v-alert>
      </v-card-text>

      <v-card-actions v-if="options.length">
        <v-btn block color="#483CB9" :dark="canSave" :loading="loading" :disabled="!canSave" @click="save">
          Сохранить
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { utcToLocalTime } from '@/common/calendarTime';
import { appointmentExpertId, expertFullName, initials } from './appointmentHelpers';

// Смена эксперта записи. Кандидаты — активные эксперты с активной услугой того же типа,
// что и у записи. Бэк примет только эксперта с открытым интервалом, покрывающим всё время
// записи (и без этой услуги в unavailable_services), иначе 409 — таких показываем недоступными.
// Окончательно решает бэк, его ошибка выводится в диалоге.
export default {
  name: 'ChangeExpertDialog',
  props: {
    value: { type: Boolean, default: false },
    appointment: { type: Object, default: null },
    experts: { type: Array, default: () => [] },
    services: { type: Array, default: () => [] },
    availability: { type: Array, default: () => [] }, // открытые интервалы
    serviceTypes: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    errors: { type: Array, default: () => [] }, // Object.entries ответа 400
  },
  data() {
    return {
      expertId: null,
    };
  },
  computed: {
    currentExpertId() {
      return this.appointment ? appointmentExpertId(this.appointment, this.services) : null;
    },
    currentExpertName() {
      return expertFullName(this.experts.find((expert) => expert.id === this.currentExpertId));
    },
    // Тип услуги записи: через забронированную услугу эксперта, иначе — по коду типа.
    serviceTypeId() {
      if (!this.appointment) return null;
      const service = this.services.find((item) => item.id === this.appointment.service);
      if (service) return service.service_type;
      const type = this.serviceTypes.find((item) => item.code === this.appointment.service_type_code);
      return type ? type.id : null;
    },
    options() {
      if (!this.serviceTypeId) return [];
      const duration = this.appointment.allocated_duration_minutes || 0;
      return this.experts
        .filter((expert) => expert.is_active !== false && expert.id !== this.currentExpertId)
        .map((expert) => {
          const service = this.services.find((item) => item.expert === expert.id
            && item.service_type === this.serviceTypeId
            && item.is_active !== false);
          if (!service) return null;
          const name = expertFullName(expert);
          let hint = '';
          if (duration && service.max_duration_minutes < duration) {
            hint = `Услуга до ${service.max_duration_minutes} мин — меньше длительности записи (${duration} мин)`;
          } else if (!this.hasFreeInterval(expert.id, service.id)) {
            hint = 'Нет свободного интервала на это время';
          }
          return {
            id: expert.id,
            name,
            initials: initials(name),
            photoUrl: expert.photo_url,
            disabled: Boolean(hint),
            hint: hint || 'Свободен в это время',
          };
        })
        .filter(Boolean)
        .sort((a, b) => Number(a.disabled) - Number(b.disabled) || a.name.localeCompare(b.name));
    },
    whenText() {
      if (!this.appointment) return '';
      const start = this.$moment.utc(this.appointment.scheduled_starts_at).local().locale('ru').format('D MMMM YYYY');
      return `${start}, ${utcToLocalTime(this.appointment.scheduled_starts_at)} – ${utcToLocalTime(this.appointment.scheduled_ends_at)}`;
    },
    errorMessages() {
      return this.errors.reduce((acc, [, messages]) => acc.concat(messages), []);
    },
    canSave() {
      return Boolean(this.expertId) && !this.loading;
    },
  },
  watch: {
    // Диалог не пересоздаётся между открытиями — сбрасываем выбор при каждом показе.
    value(isOpen) {
      if (isOpen) this.expertId = null;
    },
    expertId() {
      if (this.errors.length) this.$emit('clear-errors');
    },
  },
  methods: {
    // Открытый интервал эксперта целиком покрывает время записи, услуга в нём не на стопе.
    hasFreeInterval(expertId, serviceId) {
      const { scheduled_starts_at: startsAt, scheduled_ends_at: endsAt } = this.appointment;
      return this.availability.some((item) => item.expert === expertId
        && item.status === 'open'
        && !(item.unavailable_services || []).includes(serviceId)
        && !this.$moment.utc(item.starts_at).isAfter(this.$moment.utc(startsAt))
        && !this.$moment.utc(item.ends_at).isBefore(this.$moment.utc(endsAt)));
    },
    save() {
      if (!this.canSave) return;
      this.$emit('save', this.expertId);
    },
  },
};
</script>

<style lang="scss" scoped>
.change-expert__initials {
  font-size: 10px;
}

.change-expert__option {
  line-height: 1.3;
  padding: 4px 0;
}

.change-expert__empty {
  font-size: 14px;
  color: #8a8f9c;
  background: #f7f7f9;
  border-radius: 8px;
  padding: 12px;
}
</style>
